import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Button, ButtonProps, Center, Separator, Heading, Icon, Portal, Spinner, VStack, Dialog, Field, NativeSelect } from '@chakra-ui/react';
import { AutoResizeTextarea } from '@src/components/sco/AutoResizeTextarea';
import { Paragraph } from '@src/components/sco/Paragraph';
import { groupRequestVisibilityTypes } from '@src/constants/group-request-visibility-types';
import { UpsertGroupRequestInput } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import { omit } from 'lodash';
import { parseAsString, useQueryStates } from 'nuqs';
import React, { useEffect, useState } from 'react';
import { LuCheck, LuHourglass } from 'react-icons/lu';
const PUBLISH_REQUEST_FORM_FRAGMENT = gql`
  fragment _ on GroupRequest {
    id
    groupRequestId
    publishApprovedAt
    publishDeniedAt
    publishDeniedNotes
    publishRequestedAt
    publishRequestedNotes
    publishedAt
    publishStatusKey
    publishStatus {
      id
      title
      key
    }
  }
`;
export type PublishRequestFormProps = {
  groupRequestId?: string;
};
export const PublishRequestForm = (props: PublishRequestFormProps) => {
  const {
    data,
    loading
  } = useQuery(gql`
      query ($groupRequestId: String!) {
        groupRequest(groupRequestId: $groupRequestId) {
          id
          ${PUBLISH_REQUEST_FORM_FRAGMENT}
        }
      }
    `, {
    variables: {
      groupRequestId: props.groupRequestId
    },
    skip: !props.groupRequestId
  });
  const groupRequest = data?.groupRequest;
  const [upsertGroupRequest, upsertGroupRequestMutation] = useMutation(gql`
    mutation ($input: UpsertGroupRequestInput!) {
      upsertGroupRequest(input: $input) {
        groupRequest {
          id
          ${PUBLISH_REQUEST_FORM_FRAGMENT}
        }
      }
    }
  `);
  const [visibilityKey, setVisibilityKey] = useState<string>(groupRequest?.visibilityKey);
  const [publishRequestedNotes, setPublishRequestedNotes] = useState<string>(groupRequest?.publishRequestedNotes || '');
  const [upserted, setUpserted] = useState<boolean>(false);
  useEffect(() => {
    if (groupRequest?.publishRequestedNotes) {
      setPublishRequestedNotes(groupRequest.publishRequestedNotes);
    }
  }, [groupRequest?.publishRequestedNotes]);
  const upsertGroupRequestMutationLoading = (upsertGroupRequestMutation as any).loading;
  const handleSubmit = async () => {
    if (!props.groupRequestId) {
      return;
    }
    const input: UpsertGroupRequestInput = {
      groupRequestId: props.groupRequestId,
      visibilityKey: visibilityKey,
      publishRequestedNotes: publishRequestedNotes
    };
    if (groupRequest.publishStatusKey === 'draft' || groupRequest.publishStatusKey === 'rejected') {
      input.publishStatusKey = 'pending_approval';
    }
    await upsertGroupRequest({
      variables: {
        input
      }
    });
    setUpserted(true);
  };
  if (loading) {
    return <Center padding={8}>
        <Spinner />
      </Center>;
  }
  if (groupRequest?.publishedAt || groupRequest?.publishStatusKey === 'published') {
    return <VStack align={'center'} gap={4} paddingY={8}>
        <Icon asChild boxSize={12} color={'green.600'}>
          <LuCheck />
        </Icon>
        <Heading>Request is published.</Heading>
      </VStack>;
  }
  if ((upserted || groupRequest?.publishRequestedAt) && !groupRequest?.publishDeniedAt) {
    return <VStack align={'center'} gap={4} paddingY={8}>
        <Icon asChild boxSize={12} color={'green.600'}>
          <LuCheck />
        </Icon>
        <Heading>Request submitted for review.</Heading>
        <Paragraph textAlign={'center'}>
          Our team will review and either publish it or reach out if we have any questions.
        </Paragraph>
      </VStack>;
  }
  return <VStack align={'stretch'} gap={4}>
      {groupRequest?.publishDeniedAt && <VStack align={'center'} gap={4} paddingY={8}>
          <Icon asChild boxSize={12} color={'orange'}>
            <LuHourglass />
          </Icon>
          <Heading size={'md'}>Previously denied.</Heading>
          <Paragraph textAlign={'center'}>This request was previously submitted and denied.</Paragraph>
          {groupRequest?.publishDeniedNotes && <Paragraph color={'colorPalette.fg/70'} textAlign={'center'}>
              {'Notes: '}
              {groupRequest.publishDeniedNotes}
            </Paragraph>}
        </VStack>}
      <Heading size={'md'}>Request to publish this goal publicly?</Heading>
      <Paragraph>
        Published goals appear in search results and on profile pages. Our team will review before publishing.
      </Paragraph>
      <Field.Root>
        <Field.Label>Visibility</Field.Label>
        <NativeSelect.Root>
          <NativeSelect.Field value={visibilityKey} onChange={e => setVisibilityKey(e.target.value)}>
            {groupRequestVisibilityTypes.map((visibilityType, idx) => <option key={idx} value={visibilityType.key}>
                {visibilityType.title}
              </option>)}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Field.Root>
      <Field.Root>
        <Field.Label>Any additional information you'd like our team to know?</Field.Label>
        <AutoResizeTextarea value={publishRequestedNotes} onChange={e_0 => setPublishRequestedNotes(e_0.target.value)} />
      </Field.Root>
      <Separator />
      <Button loading={upsertGroupRequestMutationLoading} size={'lg'} variant={'cta'} width={'full'} onClick={() => handleSubmit()}>
        Submit for Approval
      </Button>
    </VStack>;
};
export const PublishRequestButton = props => {
  const $ = _c(17);
  const {
    currentUserHasContentPermission
  } = useCurrentUser();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      prompt: parseAsString,
      productId: parseAsString,
      intent: parseAsString
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [, setQueryState] = useQueryStates(t0);
  const [showPublishModal, setShowPublishModal] = useState(false);
  let t1;
  if ($[1] !== currentUserHasContentPermission || $[2] !== setQueryState) {
    t1 = ev => {
      ev.stopPropagation();
      if (currentUserHasContentPermission("canAccessFGRequests")) {
        setShowPublishModal(true);
      } else {
        setQueryState({
          prompt: "checkout",
          productId: "scom-accelerator-tools",
          intent: "request-publish"
        });
      }
    };
    $[1] = currentUserHasContentPermission;
    $[2] = setQueryState;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onClick = t1;
  let t2;
  if ($[4] !== props) {
    t2 = omit(props, ["groupRequestId"]);
    $[4] = props;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = props.children || "Publish";
  let t4;
  if ($[6] !== onClick || $[7] !== t2 || $[8] !== t3) {
    t4 = <Button onClick={onClick} {...t2}>{t3}</Button>;
    $[6] = onClick;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setShowPublishModal(false);
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== props.groupRequestId || $[12] !== showPublishModal) {
    t6 = <Portal><PublishRequestModal groupRequestId={props.groupRequestId} isOpen={showPublishModal} onClose={t5} /></Portal>;
    $[11] = props.groupRequestId;
    $[12] = showPublishModal;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t4 || $[15] !== t6) {
    t7 = <>{t4}{t6}</>;
    $[14] = t4;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
export const PublishRequestModal = props => {
  const $ = _c(10);
  const t0 = !!props.isOpen;
  let t1;
  if ($[0] !== props) {
    t1 = t2 => {
      const {
        open
      } = t2;
      if (!open && props.onClose) {
        props.onClose();
      }
    };
    $[0] = props;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Dialog.Backdrop />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Dialog.CloseTrigger />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== props.groupRequestId) {
    t4 = <Dialog.Positioner><Dialog.Content bgColor="colorPalette.container" maxWidth="container.md" padding={4}>{t3}<Dialog.Body padding={4}><PublishRequestForm groupRequestId={props.groupRequestId} /></Dialog.Body></Dialog.Content></Dialog.Positioner>;
    $[4] = props.groupRequestId;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t4) {
    t5 = <Dialog.Root open={t0} onOpenChange={t1}>{t2}{t4}</Dialog.Root>;
    $[6] = t0;
    $[7] = t1;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};